import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { AuthStateService } from './auth-state.service';

/**
 * Service that monitors the user's authentication session.
 *
 * Responsibilities:
 * - Validates session on tab visibility change (user returns to tab)
 * - Validates session when network comes back online
 * - Schedules proactive token refresh before expiration
 * - Redirects to login when session is invalid
 *
 * Usage:
 * - Call `start()` after successful authentication
 * - Call `stop()` on logout or when monitoring is no longer needed
 */
@Injectable({ providedIn: 'root' })
export class SessionMonitorService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly authState = inject(AuthStateService);
  private readonly router = inject(Router);

  /** Timer ID for scheduled token refresh */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  /** Bound event handlers for cleanup */
  private readonly boundHandlers = {
    visibility: this.onVisibilityChange.bind(this),
    online: this.onOnline.bind(this),
  };

  /** Whether monitoring is currently active */
  private isMonitoring = false;

  /**
   * Start monitoring the authentication session.
   * Safe to call multiple times - will not create duplicate listeners.
   */
  start(): void {
    if (this.isMonitoring) return;

    this.isMonitoring = true;

    document.addEventListener('visibilitychange', this.boundHandlers.visibility);
    window.addEventListener('online', this.boundHandlers.online);

    // Initial session check and refresh scheduling
    this.validateAndSchedule();
  }

  /**
   * Stop monitoring the authentication session.
   * Cleans up all event listeners and timers.
   */
  stop(): void {
    if (!this.isMonitoring) return;

    this.isMonitoring = false;

    document.removeEventListener('visibilitychange', this.boundHandlers.visibility);
    window.removeEventListener('online', this.boundHandlers.online);

    this.cancelScheduledRefresh();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.validateAndSchedule();
    }
  }

  private onOnline(): void {
    this.validateAndSchedule();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Validation
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Validate the current session and schedule the next refresh.
   */
  private async validateAndSchedule(): Promise<void> {
    if (this.shouldSkipValidation()) return;

    try {
      const token = await this.auth.getAccessTokenSilently();
      this.scheduleTokenRefresh(token);
    } catch {
      this.handleSessionInvalid();
    }
  }

  /**
   * Check if session validation should be skipped.
   * Uses window.location so we skip reliably during initial load (e.g. OAuth callback)
   * when router.url may not be updated yet.
   */
  private shouldSkipValidation(): boolean {
    if (this.authState.isRedirecting()) return true;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/login')) return true;
    const search = typeof window !== 'undefined' ? window.location.search : '';
    if (search.includes('code=') && search.includes('state=')) return true;
    return false;
  }

  /**
   * Handle invalid session by redirecting to login.
   */
  private handleSessionInvalid(): void {
    if (this.shouldSkipValidation()) return;

    if (this.authState.acquireRedirectLock()) {
      this.router
        .navigate(['/login'], {
          queryParams: { error: 'session_expired' },
        })
        .finally(() => {
          this.authState.clearRedirectLockAfterDelay(1000);
        });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Token Refresh Scheduling
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Schedule a token refresh before the current token expires.
   */
  private scheduleTokenRefresh(token: string): void {
    this.cancelScheduledRefresh();

    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) return;

    const now = Date.now();
    const expiresAt = expirationTime * 1000;

    // Refresh 60 seconds before expiration, minimum 5 seconds from now
    const refreshDelay = Math.max(5_000, expiresAt - now - 60_000);

    this.refreshTimer = setTimeout(() => {
      this.validateAndSchedule();
    }, refreshDelay);
  }

  /**
   * Cancel any scheduled token refresh.
   */
  private cancelScheduledRefresh(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Extract the expiration timestamp from a JWT token.
   * Returns null if the token cannot be parsed or has no expiration.
   */
  private getTokenExpiration(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = JSON.parse(atob(parts[1]));
      const exp = Number(payload?.exp);

      return Number.isFinite(exp) ? exp : null;
    } catch {
      return null;
    }
  }
}
